import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MapPin, Gauge, Clock, DollarSign, Phone, Mail, FileText, Package, Award, Wrench,
  Download, Camera, Heart, MessageSquare, MessageCircle, Zap, Weight, Activity, CheckCircle, AlertCircle, Info,
  Star, Calendar, ChevronLeft, ChevronRight, Share2, Sparkles, Lock, Cog
} from 'lucide-react';
import { EngineModuleHealthCard } from './EngineModuleHealthCard';
import { cn } from '@/lib/utils';
import { useFavorites } from '@/hooks/useFavorites';
import { ShareListingButton } from '@/components/ShareListingButton';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import OptimizedImage from '@/components/OptimizedImage';
import enginePlaceholder from '@/assets/hero-helicopter-new.jpg';
import { useToast } from '@/hooks/use-toast';
import DocumentRequestModal from '@/components/messaging/DocumentRequestModal';
import { useDocumentAccessRequests } from '@/hooks/useDocumentAccessRequests';
import { parseBuyRequestDescription, getUrgencyLabel, getUrgencyVariant } from '@/utils/engineBuyRequestParser';
import { UniversalMap } from '@/components/maps';
import { useIsDesktop } from '@/hooks/use-tablet';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { useEffectiveVisibility } from '@/hooks/useEffectiveVisibility';
import { SellerSection } from '@/components/listing/SellerSection';
import { useViewTracking } from '@/hooks/useViewTracking';

interface EngineModule {
  id: string;
  module_name: string;
  module_type: string;
  tsn?: number;
  csn?: number;
  tso?: number;
  cso?: number;
  condition?: string;
  serial_number?: string;
  part_number?: string;
  notes?: string;
  life_limit_hours?: number;
  life_limit_cycles?: number;
  next_inspection_due?: number;
}

interface EngineListing {
  id: string;
  manufacturer: string;
  model: string;
  variant?: string;
  title: string;
  serial_number?: string;
  price_usd?: number;
  rental_rate?: number;
  rental_period?: string;
  minimum_term?: number;
  preferred_term?: number;
  deposit?: number;
  max_rate?: number;
  max_price?: number;
  maintenance_responsibility?: string;
  power_output?: number;
  power_unit?: string;
  weight?: number;
  weight_unit?: string;
  total_tsn?: number;
  total_csn?: number;
  last_overhaul_tso?: number;
  last_overhaul_cso?: number;
  recommended_tbo?: number;
  overall_condition?: string;
  logbooks_available?: boolean;
  accident_free?: boolean;
  maintenance_history?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  shipping_options?: string[];
  certifications?: string[];
  listing_type: string;
  photos?: string[];
  documents?: string[];
  description?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  user_id?: string;
  flexible_price?: boolean;
  featured?: boolean;
  // Technical specs
  sfc?: number;
  pressure_ratio?: number;
  turbine_inlet_temp?: number;
  // Privacy settings
  visibility_settings?: {
    use_global_defaults?: boolean;
    hide_price?: boolean;
    hide_location?: boolean;
    hide_serial?: boolean;
    hide_hours?: boolean;
    hide_contact?: boolean;
  };
}

interface EngineListingDetailModalProps {
  listing: EngineListing | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sellerName?: string;
  sellerId?: string;
}

const EngineListingDetailModal: React.FC<EngineListingDetailModalProps> = ({
  listing,
  open,
  onOpenChange,
  sellerId,
}) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const [allImages, setAllImages] = useState<string[]>([]);
  
  // Track views when modal opens
  useViewTracking(listing?.id || null, open);
  const [documents, setDocuments] = useState<any[]>([]);
  const [modules, setModules] = useState<EngineModule[]>([]);
  const [showDocRequestModal, setShowDocRequestModal] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sellerProfile, setSellerProfile] = useState<any>(null);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const { toast } = useToast();
  const { createRequest } = useDocumentAccessRequests(conversationId || '');
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const { openUnifiedContact } = useUnifiedContact();

  const effectiveSellerId = sellerId ?? listing?.user_id;
  const {
    blurPrice: shouldHidePrice,
    blurLocation: shouldHideLocation,
    blurSerial: shouldHideSerial,
    blurHours: shouldHideHours,
    blurContact: shouldHideContact,
  } = useEffectiveVisibility(listing?.id, listing?.visibility_settings, effectiveSellerId);

  if (!listing) return null;

  const isOwner = user?.id === effectiveSellerId;
  const isBuyRequest = listing.listing_type === 'engine_buy' || listing.listing_type === 'engine_rent_request';
  const buyRequestMeta = isBuyRequest ? parseBuyRequestDescription(listing.description) : null;

  // Handler for Contact Seller using unified modal
  const handleContact = () => {
    // Auth gate: redirect to sign-in if not logged in
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to contact this seller' }
      }));
      return;
    }
    const confidentialDocs = documents
      .filter(doc => doc.is_confidential)
      .map(doc => ({
        id: doc.id,
        document_name: doc.document_name,
        document_category: doc.document_category,
        has_access: doc.has_access,
      }));
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: effectiveSellerId || '',
      assetType: 'engine',
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        serial: shouldHideSerial,
        hours: shouldHideHours,
        contact: shouldHideContact,
      },
      confidentialDocuments: confidentialDocs.length > 0 ? confidentialDocs : undefined,
    });
    onOpenChange(false);
  };

  // Helper to request a specific field with preselection (auth-gated)
  const requestField = (field: string) => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to request this information' }
      }));
      return;
    }
    const confidentialDocs = documents
      .filter(doc => doc.is_confidential)
      .map(doc => ({
        id: doc.id,
        document_name: doc.document_name,
        document_category: doc.document_category,
        has_access: doc.has_access,
      }));
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: effectiveSellerId || '',
      assetType: 'engine',
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        serial: shouldHideSerial,
        hours: shouldHideHours,
        contact: shouldHideContact,
      },
      preselectedFields: [field],
      confidentialDocuments: confidentialDocs.length > 0 ? confidentialDocs : undefined,
    });
    onOpenChange(false);
  };

  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!listing.id) return;
      try {
        // Fetch seller profile - use effectiveSellerId which falls back to listing.user_id
        if (effectiveSellerId) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('id, user_id, display_name, avatar_url, company_name, company_logo_url, represent_as_company, user_type, is_verified, created_at, dealer_status')
            .eq('user_id', effectiveSellerId)
            .maybeSingle();
          setSellerProfile(profileData);
        }
        
        const [imagesData, modulesData, convData] = await Promise.all([
          supabase.from('listing_media').select('file_url, display_order, metadata, created_at').eq('listing_id', listing.id).eq('media_type', 'photo').order('display_order', { ascending: true }),
          supabase.from('engine_modules').select('*').eq('engine_listing_id', listing.id).order('display_order', { ascending: true }),
          user ? supabase.from('conversations').select('id').eq('listing_id', listing.id).eq('buyer_id', user.id).maybeSingle() : Promise.resolve({ data: null, error: null })
        ]);
        
        // Fetch documents using edge function for secure access with has_access flag
        let documentsData: any[] = [];
        try {
          const { data: session } = await supabase.auth.getSession();
          const response = await fetch('https://dvawogvuaomipakwlvst.supabase.co/functions/v1/get-listing-documents', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(session?.session?.access_token && {
                'Authorization': `Bearer ${session.session.access_token}`
              })
            },
            body: JSON.stringify({ listingId: listing.id, assetType: 'engine' })
          });
          
          if (response.ok) {
            const result = await response.json();
            documentsData = result.documents || [];
          }
        } catch (docError) {
          console.error('Error fetching documents via edge function:', docError);
          // Fallback to direct fetch (will only work for owners)
          const { data: fallbackDocs } = await supabase
            .from('listing_documents')
            .select('*')
            .eq('listing_id', listing.id);
          documentsData = fallbackDocs || [];
        }
        
        // ONLY use listing_media for engines - never fall back to listing.photos
        if (imagesData.data?.length) {
          // Find the best hero: newest with category === 'hero'
          const heroItems = imagesData.data.filter(img => (img.metadata as any)?.category === 'hero');
          const bestHero = heroItems.length > 0
            ? heroItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
            : null;
          
          // Deduplicate and put hero first
          const uniqueUrls = [...new Set(imagesData.data.map(img => img.file_url))];
          if (bestHero) {
            const heroUrl = bestHero.file_url;
            const orderedImages = [heroUrl, ...uniqueUrls.filter(url => url !== heroUrl)];
            setAllImages(orderedImages);
          } else {
            setAllImages(uniqueUrls);
          }
        } else {
          // No listing_media found - show empty (no fallback to listing.photos)
          setAllImages([]);
        }
        setDocuments(documentsData);
        if (!modulesData.error) setModules(modulesData.data || []);
        if (convData.data) setConversationId(convData.data.id);
      } catch (error) {
        console.error('Error fetching additional data:', error);
      }
    };
    if (open) {
      setSelectedPhotoIndex(0);
      fetchAdditionalData();
    }
  }, [listing.id, open, user, effectiveSellerId]);

  const handleFavoriteToggle = async () => { await toggleFavorite(listing.id, 'engine'); };
  const formatNumber = (num?: number) => num ? new Intl.NumberFormat('en-US').format(num) : 'N/A';
  
  const formatShippingOption = (option: string) => {
    const labels: Record<string, string> = {
      local_pickup: 'Local Pickup',
      ground: 'Ground Shipping',
      air_freight: 'Air Freight',
      international: 'International Shipping',
    };
    return labels[option] || option.replace(/_/g, ' ');
  };

  const getListingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      engine_sell: 'For Sale',
      engine_rent: 'For Rent',
      engine_buy: 'Wanted to Buy',
      engine_rent_request: 'Wanted to Rent'
    };
    return labels[type] || type;
  };

  const getListingTypeBadgeStyle = (type: string) => {
    if (type === 'engine_sell') return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
    if (type === 'engine_rent') return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
    if (type === 'engine_buy' || type === 'engine_rent_request') return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20';
    return 'bg-muted text-muted-foreground';
  };
  
  const getConditionColor = (condition?: string) => {
    const colors: Record<string, string> = {
      new: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20',
      overhauled: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
      serviceable: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
      needs_repair: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    };
    return colors[condition || ''] || 'bg-muted text-muted-foreground border-muted';
  };

  const displayImages = allImages;
  const hasImages = displayImages.length > 0;

  const nextImage = () => setSelectedPhotoIndex(prev => prev < displayImages.length - 1 ? prev + 1 : 0);
  const prevImage = () => setSelectedPhotoIndex(prev => prev > 0 ? prev - 1 : displayImages.length - 1);

  // Touch swipe state
  const touchStartX = React.useRef<number | null>(null);
  const touchEndX = React.useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextImage();
      else prevImage();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Professional Photo Gallery Component
  const renderPhotoGallery = () => (
    <Card className="overflow-hidden mb-4 sm:mb-6">
      <div 
        className={cn(
          "relative bg-muted",
          isMobile ? "aspect-[4/3]" : "aspect-video"
        )}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        {hasImages ? (
          <>
            <OptimizedImage 
              src={displayImages[selectedPhotoIndex]} 
              alt={listing.title} 
              className="w-full h-full object-contain touch-manipulation" 
              fallbackSrc={enginePlaceholder} 
              size="full" 
            />
            {/* Counter - centered bottom on mobile */}
            <div className={cn(
              "absolute bg-black/70 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 backdrop-blur-sm",
              isMobile ? "bottom-3 left-1/2 -translate-x-1/2" : "top-3 right-3"
            )}>
              <Camera className="w-3.5 h-3.5" />
              {selectedPhotoIndex + 1} / {displayImages.length}
            </div>
            
            {/* Dot indicators for mobile */}
            {isMobile && displayImages.length > 1 && displayImages.length <= 10 && (
              <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-1.5">
                {displayImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedPhotoIndex(idx)}
                    aria-label={`View photo ${idx + 1}`}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      idx === selectedPhotoIndex ? "bg-white scale-110" : "bg-white/50"
                    )}
                  />
                ))}
              </div>
            )}
            
            {/* Nav buttons - desktop only */}
            {!isMobile && displayImages.length > 1 && (
              <>
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white min-h-[44px] min-w-[44px] rounded-full" 
                  onClick={prevImage}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white min-h-[44px] min-w-[44px] rounded-full" 
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No photos available</p>
            </div>
          </div>
        )}
      </div>
      {hasImages && displayImages.length > 1 && (
        <div className="p-2 sm:p-3 border-t bg-muted/30 overflow-x-auto scrollbar-hide">
          <div className="flex gap-1.5 sm:gap-2">
            {displayImages.map((img, idx) => (
              <button 
                key={idx} 
                onClick={() => setSelectedPhotoIndex(idx)} 
                aria-label={`View photo ${idx + 1}`}
                className={cn(
                  "flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isMobile ? "w-12 h-12" : "w-16 h-16",
                  idx === selectedPhotoIndex ? "border-primary ring-2 ring-primary/20" : "border-transparent hover:border-muted-foreground/30"
                )}
              >
                <OptimizedImage src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" fallbackSrc={enginePlaceholder} size="thumbnail" />
              </button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );

  // Tabs Content
  const renderTabs = (compact: boolean = false) => (
    <Tabs defaultValue="overview" className="space-y-3 sm:space-y-4">
      <TabsList className={cn(
        "h-auto p-1 gap-1",
        compact 
          ? "flex overflow-x-auto scrollbar-hide justify-start w-full after:content-[''] after:w-4 after:flex-shrink-0" 
          : "grid w-full grid-cols-4"
      )}>
        <TabsTrigger value="overview" className={cn(
          "min-h-[40px] sm:min-h-[44px] data-[state=active]:bg-background",
          compact ? "flex-shrink-0 px-3 text-xs gap-1.5" : "text-xs md:text-sm"
        )}>
          {compact && <Info className="w-3.5 h-3.5" />}
          Overview
        </TabsTrigger>
        <TabsTrigger value="specs" className={cn(
          "min-h-[40px] sm:min-h-[44px] data-[state=active]:bg-background",
          compact ? "flex-shrink-0 px-3 text-xs gap-1.5" : "text-xs md:text-sm"
        )}>
          {compact && <Gauge className="w-3.5 h-3.5" />}
          Specs
        </TabsTrigger>
        <TabsTrigger value="modules" className={cn(
          "min-h-[40px] sm:min-h-[44px] data-[state=active]:bg-background",
          compact ? "flex-shrink-0 px-3 text-xs gap-1.5" : "text-xs md:text-sm"
        )}>
          {compact && <Package className="w-3.5 h-3.5" />}
          Modules
        </TabsTrigger>
        <TabsTrigger value="documents" className={cn(
          "min-h-[40px] sm:min-h-[44px] data-[state=active]:bg-background",
          compact ? "flex-shrink-0 px-3 text-xs gap-1.5" : "text-xs md:text-sm"
        )}>
          {compact && <FileText className="w-3.5 h-3.5" />}
          Docs
        </TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-4 mt-4">

        {/* Description */}
        {listing.description && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {isBuyRequest && buyRequestMeta ? buyRequestMeta.cleanDescription : listing.description}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Buy Request Metadata */}
        {isBuyRequest && buyRequestMeta && (
          <Card className="border-amber-500/20 bg-amber-500/5">
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                Request Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {buyRequestMeta.urgency && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Urgency</span>
                  <Badge variant={getUrgencyVariant(buyRequestMeta.urgency) as any}>
                    {getUrgencyLabel(buyRequestMeta.urgency)}
                  </Badge>
                </div>
              )}
              {buyRequestMeta.financing && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Financing</span>
                  <span className="text-sm font-medium">{buyRequestMeta.financing}</span>
                </div>
              )}
              {buyRequestMeta.radius && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Search Radius</span>
                  <span className="text-sm font-medium">{buyRequestMeta.radius}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Location with Map */}
        {(listing.city || listing.country) && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              {shouldHideLocation ? (
                <BlurredPrivateData
                  placeholderContent={
                    <div className="h-48 bg-muted rounded-lg flex items-center justify-center">
                      <MapPin className="w-8 h-8 text-muted-foreground" />
                    </div>
                  }
                  fieldName="Location"
                  size="lg"
                   onRequestInfo={isOwner ? undefined : () => requestField('location')}
                />
              ) : listing.latitude && listing.longitude ? (
                <UniversalMap 
                  singleMarker={{ position: { lat: listing.latitude, lng: listing.longitude }, listingType: listing.listing_type }}
                  height="h-48" 
                  assetType="engine" 
                />
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {[listing.city, listing.country].filter(Boolean).join(', ')}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contact Information */}
        {!isOwner && (listing.contact_email || listing.contact_phone || listing.contact_name) && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Seller Contact
                {shouldHideContact && <Lock className="w-3 h-3 text-muted-foreground" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {shouldHideContact ? (
                <BlurredPrivateData
                  placeholderContent={
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Contact Name</div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="min-h-[44px]">
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </Button>
                        <Button variant="outline" size="sm" className="min-h-[44px]">
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </Button>
                      </div>
                    </div>
                  }
                  fieldName="Contact Information"
                  size="md"
                   onRequestInfo={isOwner ? undefined : () => requestField('contact')}
                />
              ) : (
                <>
                  {listing.contact_name && (
                    <div className="text-sm font-medium">{listing.contact_name}</div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {listing.contact_email && (
                      <Button variant="outline" size="sm" asChild className="min-h-[44px]">
                        <a href={`mailto:${listing.contact_email}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </a>
                      </Button>
                    )}
                    {listing.contact_phone && (
                      <Button variant="outline" size="sm" asChild className="min-h-[44px]">
                        <a href={`tel:${listing.contact_phone}`}>
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </a>
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rental Terms (for rent listings) */}
        {listing.listing_type === 'engine_rent' && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Rental Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              {shouldHidePrice ? (
                <BlurredPrivateData
                  placeholderContent={
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="h-12 bg-muted rounded" />
                      <div className="h-12 bg-muted rounded" />
                    </div>
                  }
                  fieldName="Rental Terms"
                  size="lg"
                   onRequestInfo={isOwner ? undefined : () => requestField('price')}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {listing.minimum_term && (
                    <div>
                      <p className="text-xs text-muted-foreground">Minimum Term</p>
                      <p className="text-sm font-medium">{listing.minimum_term} months</p>
                    </div>
                  )}
                  {listing.preferred_term && (
                    <div>
                      <p className="text-xs text-muted-foreground">Preferred Term</p>
                      <p className="text-sm font-medium">{listing.preferred_term} months</p>
                    </div>
                  )}
                  {listing.deposit && (
                    <div>
                      <p className="text-xs text-muted-foreground">Deposit</p>
                      <p className="text-sm font-medium">${formatNumber(listing.deposit)}</p>
                    </div>
                  )}
                  {listing.maintenance_responsibility && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">Maintenance Responsibility</p>
                      <p className="text-sm font-medium capitalize">{listing.maintenance_responsibility.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Requirements Section for Buy/Rent Requests */}
        {isBuyRequest && (
          <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5">
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                Requirements (Maximum Acceptable)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Desired Condition */}
              {listing.overall_condition && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Desired Condition</p>
                  <Badge className={cn(getConditionColor(listing.overall_condition), 'capitalize')}>
                    {listing.overall_condition.replace(/_/g, ' ')}
                  </Badge>
                </div>
              )}
              
              {/* Max Time/Cycles Requirements */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {listing.total_tsn != null && (
                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <p className="text-xs text-muted-foreground">Max TSN</p>
                    <p className="font-semibold text-blue-700 dark:text-blue-400">{formatNumber(listing.total_tsn)} hrs</p>
                  </div>
                )}
                {listing.total_csn != null && (
                  <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <p className="text-xs text-muted-foreground">Max CSN</p>
                    <p className="font-semibold text-purple-700 dark:text-purple-400">{formatNumber(listing.total_csn)}</p>
                  </div>
                )}
                {listing.last_overhaul_tso != null && (
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <p className="text-xs text-muted-foreground">Max TSO</p>
                    <p className="font-semibold text-emerald-700 dark:text-emerald-400">{formatNumber(listing.last_overhaul_tso)} hrs</p>
                  </div>
                )}
                {listing.last_overhaul_cso != null && (
                  <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <p className="text-xs text-muted-foreground">Max CSO</p>
                    <p className="font-semibold text-amber-700 dark:text-amber-400">{formatNumber(listing.last_overhaul_cso)}</p>
                  </div>
                )}
              </div>

              {/* Documentation Requirements */}
              <div className="flex flex-wrap gap-2">
                {listing.logbooks_available && (
                  <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Complete Logbooks Required
                  </Badge>
                )}
                {listing.accident_free && (
                  <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Accident-Free Required
                  </Badge>
                )}
                {listing.flexible_price && (
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20">
                    <DollarSign className="w-3 h-3 mr-1" />
                    Flexible Budget
                  </Badge>
                )}
              </div>

              {/* Required Certifications */}
              {listing.certifications && listing.certifications.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Required Certifications</p>
                  <div className="flex flex-wrap gap-2">
                    {listing.certifications.map((cert, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-primary/10 text-primary">
                        {cert}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Rent Request specific: max rate, preferred term */}
              {listing.listing_type === 'engine_rent_request' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t">
                  {listing.max_rate && (
                    <div>
                      <p className="text-xs text-muted-foreground">Max Rate</p>
                      <p className="font-semibold">${formatNumber(listing.max_rate)}/{listing.rental_period || 'month'}</p>
                    </div>
                  )}
                  {listing.preferred_term && (
                    <div>
                      <p className="text-xs text-muted-foreground">Preferred Term</p>
                      <p className="font-semibold">{listing.preferred_term} months</p>
                    </div>
                  )}
                  {listing.deposit && (
                    <div>
                      <p className="text-xs text-muted-foreground">Deposit Available</p>
                      <p className="font-semibold">${formatNumber(listing.deposit)}</p>
                    </div>
                  )}
                  {listing.maintenance_responsibility && (
                    <div>
                      <p className="text-xs text-muted-foreground">Maintenance Pref.</p>
                      <p className="font-semibold capitalize">{listing.maintenance_responsibility.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Maintenance History */}
        {listing.maintenance_history && !isBuyRequest && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Maintenance History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {listing.maintenance_history}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Certifications for Sell/Rent listings */}
        {!isBuyRequest && listing.certifications && listing.certifications.length > 0 && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-4 h-4" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {listing.certifications.map((cert, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-primary/10 text-primary">
                    {cert}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="specs" className="space-y-4 mt-4">
        <Card className="overflow-hidden">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {listing.power_output && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                <Zap className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Power Output</p>
                  <p className="font-semibold">{formatNumber(listing.power_output)} {listing.power_unit || 'shp'}</p>
                </div>
              </div>
            )}
            {listing.total_tsn != null && !shouldHideHours && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/20">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="text-xs text-muted-foreground">TSN</p>
                  <p className="font-semibold text-blue-700 dark:text-blue-400">{formatNumber(listing.total_tsn)} hrs</p>
                </div>
              </div>
            )}
            {listing.total_csn != null && !shouldHideHours && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/20">
                <Gauge className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <div>
                  <p className="text-xs text-muted-foreground">CSN</p>
                  <p className="font-semibold text-purple-700 dark:text-purple-400">{formatNumber(listing.total_csn)}</p>
                </div>
              </div>
            )}
            {listing.last_overhaul_tso != null && !shouldHideHours && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-lg border border-emerald-500/20">
                <Wrench className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <div>
                  <p className="text-xs text-muted-foreground">TSO</p>
                  <p className="font-semibold text-emerald-700 dark:text-emerald-400">{formatNumber(listing.last_overhaul_tso)} hrs</p>
                </div>
              </div>
            )}
            {listing.last_overhaul_cso != null && !shouldHideHours && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-lg border border-amber-500/20">
                <Activity className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <div>
                  <p className="text-xs text-muted-foreground">CSO</p>
                  <p className="font-semibold text-amber-700 dark:text-amber-400">{formatNumber(listing.last_overhaul_cso)}</p>
                </div>
              </div>
            )}
            {listing.recommended_tbo && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-sky-500/10 to-sky-500/5 rounded-lg border border-sky-500/20">
                <Award className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                <div>
                  <p className="text-xs text-muted-foreground">TBO</p>
                  <p className="font-semibold text-sky-700 dark:text-sky-400">{formatNumber(listing.recommended_tbo)} hrs</p>
                </div>
              </div>
            )}
            {listing.weight && (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Weight className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Weight</p>
                  <p className="font-semibold">{formatNumber(listing.weight)} {listing.weight_unit || 'kg'}</p>
                </div>
              </div>
            )}
            {listing.sfc && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-rose-500/10 to-rose-500/5 rounded-lg border border-rose-500/20">
                <Activity className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                <div>
                  <p className="text-xs text-muted-foreground">SFC</p>
                  <p className="font-semibold text-rose-700 dark:text-rose-400">{listing.sfc.toFixed(3)} lb/hp·hr</p>
                </div>
              </div>
            )}
            {listing.pressure_ratio && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 rounded-lg border border-indigo-500/20">
                <Gauge className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <div>
                  <p className="text-xs text-muted-foreground">Pressure Ratio</p>
                  <p className="font-semibold text-indigo-700 dark:text-indigo-400">{listing.pressure_ratio.toFixed(1)}:1</p>
                </div>
              </div>
            )}
            {listing.turbine_inlet_temp && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-orange-500/10 to-orange-500/5 rounded-lg border border-orange-500/20">
                <Sparkles className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <div>
                  <p className="text-xs text-muted-foreground">Turbine Inlet Temp</p>
                  <p className="font-semibold text-orange-700 dark:text-orange-400">{formatNumber(listing.turbine_inlet_temp)} K</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Details */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Additional Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {listing.serial_number && !shouldHideSerial && (
              <div>
                <p className="text-xs text-muted-foreground">Serial Number</p>
                <p className="font-mono font-medium">{listing.serial_number}</p>
              </div>
            )}
            {listing.logbooks_available !== undefined && (
              <div className="flex items-center gap-2">
                {listing.logbooks_available ? (
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="text-sm">Logbooks {listing.logbooks_available ? 'Available' : 'Unavailable'}</span>
              </div>
            )}
            {listing.accident_free !== undefined && (
              <div className="flex items-center gap-2">
                {listing.accident_free ? (
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                )}
                <span className="text-sm">{listing.accident_free ? 'Accident Free' : 'Has Damage History'}</span>
              </div>
            )}
            {listing.shipping_options && listing.shipping_options.length > 0 && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground mb-2">Shipping Options</p>
                <div className="flex flex-wrap gap-2">
                  {listing.shipping_options.map((opt, idx) => (
                    <Badge key={idx} variant="outline">{formatShippingOption(opt)}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="modules" className="space-y-4 mt-4">
        {shouldHideHours ? (
          <Card>
            <CardContent className="p-8">
              <BlurredPrivateData
                placeholderContent={
                  <div className="text-center space-y-3">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground opacity-50" />
                    <div className="space-y-1">
                      <p className="font-medium">Module Health Data</p>
                      <p className="text-sm text-muted-foreground">{modules.length} module{modules.length !== 1 ? 's' : ''} tracked</p>
                    </div>
                  </div>
                }
                fieldName="Module Data"
                size="lg"
                 onRequestInfo={isOwner ? undefined : () => requestField('modules')}
              />
            </CardContent>
          </Card>
        ) : modules.length > 0 ? (
          <div className="grid grid-cols-1 gap-3">
            {modules.map(module => (
              <EngineModuleHealthCard 
                key={module.id} 
                module={{ 
                  id: module.id, 
                  name: module.module_name, 
                  tsn: module.tsn ?? null, 
                  csn: module.csn ?? null, 
                  tso: module.tso ?? null, 
                  cso: module.cso ?? null, 
                  lifeLimitHours: module.life_limit_hours ?? null, 
                  lifeLimitCycles: module.life_limit_cycles ?? null, 
                  condition: module.condition as 'new' | 'overhauled' | 'serviceable' | 'needs_repair' | null, 
                  serialNumber: module.serial_number, 
                  notes: module.notes 
                }} 
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {isBuyRequest ? 'Module data not applicable for buy requests' : 'No module data available'}
              </p>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="documents" className="space-y-4 mt-4">
        {documents.length > 0 ? (
          <div className="space-y-2">
            {documents.map(doc => {
              // Document is locked if: confidential AND not owner AND no access grant
              const isLocked = doc.is_confidential && !isOwner && !doc.has_access;
              return (
                <Card key={doc.id} className="p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                        isLocked ? "bg-blue-500/10" : "bg-primary/10"
                      )}>
                        {isLocked ? <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400" /> : <FileText className="w-5 h-5 text-primary" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{doc.document_name}</p>
                        <div className="flex items-center gap-2">
                          {doc.document_category && (
                            <p className="text-xs text-muted-foreground">{doc.document_category}</p>
                          )}
                          {doc.is_confidential && (
                            <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20">Confidential</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {isLocked ? (
                      <Button variant="outline" size="sm" onClick={handleContact} className="min-h-[40px] flex-shrink-0">
                        <Lock className="w-4 h-4 mr-2" />
                        Request Access
                      </Button>
                    ) : doc.file_url ? (
                      <Button variant="outline" size="sm" asChild className="min-h-[40px] flex-shrink-0">
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </a>
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled className="min-h-[40px] flex-shrink-0">
                        <Download className="w-4 h-4 mr-2" />
                        Unavailable
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No documents available</p>
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );

  // Price display helper
  const renderPrice = () => {
    if (shouldHidePrice) {
      return (
        <BlurredPrivateData
          placeholderContent={
            <div>
              <p className="text-xs text-muted-foreground mb-1">{isBuyRequest ? 'Budget' : 'Price'}</p>
              <p className="text-2xl md:text-3xl font-bold text-muted-foreground">$XX,XXX</p>
            </div>
          }
          fieldName="Price"
          size="lg"
           onRequestInfo={isOwner ? undefined : () => requestField('price')}
        />
      );
    }
    
    if (listing.listing_type === 'engine_sell' && listing.price_usd) {
      return (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Asking Price</p>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            ${formatNumber(listing.price_usd)}
          </p>
          {listing.flexible_price && (
            <Badge variant="secondary" className="mt-1">Negotiable</Badge>
          )}
        </div>
      );
    }
    if (listing.listing_type === 'engine_rent' && listing.rental_rate) {
      return (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Rental Rate</p>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            ${formatNumber(listing.rental_rate)}
            <span className="text-base font-normal text-muted-foreground">/{listing.rental_period || 'month'}</span>
          </p>
        </div>
      );
    }
    if (isBuyRequest && listing.max_price) {
      return (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Budget</p>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
            Up to ${formatNumber(listing.max_price)}
          </p>
        </div>
      );
    }
    return (
      <p className="text-lg text-muted-foreground">Price on request</p>
    );
  };

  // Mobile version with Drawer
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} handleOnly={true}>
        <DrawerContent className="h-[100svh] max-h-[100svh] flex flex-col z-[100]" showCloseButton={false} showHandle={false}>
          {/* Sticky Back Button Header - flush at top */}
          <div className="sticky top-0 z-50 bg-background border-b border-border px-3 py-2.5 flex items-center gap-3 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-10 w-10 flex-shrink-0 hover:bg-muted"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <span className="font-medium text-sm text-foreground/80 truncate flex-1">Back to Marketplace</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleFavoriteToggle} aria-label={isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}>
                <Heart className={cn("w-4 h-4", isFavorite(listing.id) && "fill-red-500 text-red-500")} />
              </Button>
              <ShareListingButton
                listingId={listing.id}
                title={listing.title}
                price={listing.price_usd || listing.rental_rate || 0}
                location={[listing.city, listing.country].filter(Boolean).join(', ')}
                listingType={listing.listing_type}
                assetType="engine"
                variant="ghost"
                size="icon"
                className="h-9 w-9"
              />
            </div>
          </div>
          
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className="px-4 pb-32 overflow-x-hidden">
              {/* Header section - Mobile optimized */}
              <div className="pt-4 pb-4 space-y-5">
                  
                  {/* Title Section */}
                  <div className="bg-muted/30 rounded-lg border border-border p-3">
                    <div className="flex items-center gap-2">
                      {/* Icon */}
                      <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center border border-border shadow-sm flex-shrink-0">
                        <Cog className="w-5 h-5 text-muted-foreground" />
                      </div>
                      
                      {/* Title and badges */}
                      <div className="flex-1 min-w-0">
                        <DrawerTitle className="text-xl font-semibold text-foreground mb-2">
                          {listing.manufacturer} {listing.model}
                          {listing.variant && ` ${listing.variant}`}
                        </DrawerTitle>
                        <DrawerDescription className="sr-only">
                          {getListingTypeLabel(listing.listing_type)} listing for {listing.manufacturer} {listing.model}
                        </DrawerDescription>
                        
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className={cn("text-xs px-2 py-0.5 font-normal", getListingTypeBadgeStyle(listing.listing_type))}>
                            {getListingTypeLabel(listing.listing_type)}
                          </Badge>
                          
                          {listing.overall_condition && (
                            <Badge variant="outline" className={cn("text-xs px-2 py-0.5 font-normal capitalize", getConditionColor(listing.overall_condition))}>
                              {listing.overall_condition.replace(/_/g, ' ')}
                            </Badge>
                          )}
                          
                          {listing.featured && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Title and Description */}
                    {listing.title && (
                      <div className="mt-2 text-foreground text-base font-medium">
                        {listing.title}
                      </div>
                    )}
                    {listing.serial_number && !shouldHideSerial && (
                      <p className="mt-1 text-muted-foreground text-sm">
                        S/N: {listing.serial_number}
                      </p>
                    )}
                    {listing.description && (
                      <p className="mt-1 text-muted-foreground text-sm line-clamp-3">
                        {listing.description}
                      </p>
                    )}
                  </div>

                  {/* Seller Section */}
                  {sellerProfile && (
                    <SellerSection profile={sellerProfile} className="mt-4" onNavigate={() => onOpenChange(false)} />
                  )}

                  {/* Metadata Card - properly contained */}
                  <div className="bg-background rounded-xl border border-border p-4 shadow-sm space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                      {shouldHideLocation ? (
                        <BlurredPrivateData placeholderContent={<span className="font-medium">City, Country</span>} fieldName="Location" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('location')} />
                      ) : (listing.city || listing.country) ? (
                        <span className="font-medium break-words">{[listing.city, listing.country].filter(Boolean).join(', ')}</span>
                      ) : (
                        <span className="font-medium text-muted-foreground">Location not specified</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                       {shouldHideHours ? (
                         <div className="flex items-center gap-2">
                           <Clock className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                           <BlurredPrivateData placeholderContent={<span>X,XXX TSN</span>} fieldName="Time Data" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('hours')} />
                         </div>
                       ) : (
                         <>
                           {listing.total_tsn != null && (
                             <div className="flex items-center gap-2">
                               <Clock className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                               <span>{isBuyRequest ? 'Max ' : ''}{formatNumber(listing.total_tsn)} TSN</span>
                             </div>
                           )}
                           {listing.total_csn != null && (
                             <div className="flex items-center gap-2">
                               <span>{isBuyRequest ? 'Max ' : ''}{formatNumber(listing.total_csn)} CSN</span>
                             </div>
                           )}
                         </>
                       )}
                      {listing.power_output && (
                        <div className="flex items-center gap-2">
                          <Gauge className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                          <span>{formatNumber(listing.power_output)} {listing.power_unit || 'SHP'}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Price & Actions Section */}
                  <div className="bg-muted/50 rounded-lg border border-border p-3">
                    {/* Price/Budget section */}
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground mb-1">{isBuyRequest ? 'Budget' : listing.rental_rate ? 'Rate' : 'Price'}</p>
                      {shouldHidePrice ? (
                        <BlurredPrivateData 
                          placeholderContent={<span className="text-2xl font-semibold text-foreground">$XX,XXX</span>} 
                          fieldName="Price" 
                          size="lg"
                           onRequestInfo={isOwner ? undefined : () => requestField('price')}
                        />
                      ) : (
                        <p className="text-2xl font-semibold text-foreground">
                          {listing.price_usd ? `$${formatNumber(listing.price_usd)}` :
                           listing.rental_rate ? `$${formatNumber(listing.rental_rate)}/${listing.rental_period || 'mo'}` :
                           listing.max_price ? `Up to $${formatNumber(listing.max_price)}` : 'Price on request'}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions section - stacked for mobile */}
                    <div className="space-y-2">
                      <Button 
                        className={cn("w-full", isOwner ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" : "bg-foreground hover:bg-foreground/90 text-background")}
                        onClick={isOwner ? undefined : handleContact}
                        disabled={isOwner}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        {isOwner ? 'This is your listing' : isBuyRequest ? 'Contact Buyer' : 'Contact Seller'}
                      </Button>
                      
                      {isBuyRequest && !isOwner && (
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            const event = new CustomEvent('open-engine-buy-request-response', { detail: { request: listing } });
                            window.dispatchEvent(event);
                          }}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Offer Your Engine
                        </Button>
                      )}
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={handleFavoriteToggle} className="flex-1 border-border" aria-label={isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}>
                          <Heart className={cn("w-4 h-4", isFavorite(listing.id) ? "fill-red-500 text-red-500" : "")} />
                        </Button>
                        
                        <ShareListingButton 
                          listingId={listing.id} 
                          title={listing.title} 
                          price={listing.price_usd || listing.rental_rate || listing.max_price || 0} 
                          location={`${listing.city}, ${listing.country}`} 
                          listingType={listing.listing_type} 
                          assetType="engine"
                          variant="outline" 
                          size="icon"
                          className="flex-1 border-border"
                        />
                      </div>
                    </div>
                  </div>

                </div>

              {/* Content area - Mobile optimized */}
              <div className="space-y-6">
                {renderPhotoGallery()}
                {renderTabs(true)}
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop version with Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-background to-muted/20 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-3">
              {/* Badges Row */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={cn("text-sm", getListingTypeBadgeStyle(listing.listing_type))}>
                  {getListingTypeLabel(listing.listing_type)}
                </Badge>
                {listing.overall_condition && (
                  <Badge variant="outline" className={cn("gap-1 capitalize", getConditionColor(listing.overall_condition))}>
                    {listing.overall_condition.replace(/_/g, ' ')}
                  </Badge>
                )}
                {listing.certifications?.map((cert) => (
                  <Badge key={cert} variant="outline" className="gap-1">
                    <Award className="w-3 h-3" />
                    {cert}
                  </Badge>
                ))}
                {listing.featured && (
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md">
                    <Star className="w-3 h-3 mr-1 fill-current" /> Featured
                  </Badge>
                )}
              </div>
              
              {/* Title */}
              <DialogTitle className="text-2xl md:text-3xl font-bold leading-tight">{listing.title}</DialogTitle>
              <DialogDescription className="sr-only">
                {getListingTypeLabel(listing.listing_type)} listing for {listing.manufacturer} {listing.model}
              </DialogDescription>
              <p className="text-sm text-muted-foreground">
                {listing.manufacturer} {listing.model}
                {listing.variant && ` ${listing.variant}`}
                {listing.serial_number && !shouldHideSerial && ` • S/N: ${listing.serial_number}`}
              </p>

              {/* Metadata Row */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {shouldHideLocation ? (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    <BlurredPrivateData placeholderContent={<span>City, Country</span>} fieldName="Location" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('location')} />
                  </div>
                ) : (listing.city || listing.country) && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    <span>{[listing.city, listing.country].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {listing.total_tsn != null && !shouldHideHours && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>{isBuyRequest ? 'Max ' : ''}{formatNumber(listing.total_tsn)} hrs</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="outline" onClick={handleFavoriteToggle} className="gap-2 min-h-[44px]" aria-label={isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}>
                <Heart className={cn("w-4 h-4", isFavorite(listing.id) ? "fill-red-500 text-red-500" : "")} />
                <span className="hidden sm:inline">Save</span>
              </Button>
              <ShareListingButton 
                listingId={listing.id} 
                title={listing.title} 
                price={listing.price_usd || listing.rental_rate || listing.max_price || 0} 
                location={`${listing.city}, ${listing.country}`} 
                listingType={listing.listing_type} 
                assetType="engine"
                variant="outline"
                className="min-h-[44px]"
              />
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-6">
            {/* Price Card */}
            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 mb-6">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                {renderPrice()}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button 
                    size="lg" 
                    className={cn("min-h-[48px]", isOwner ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" : "")}
                    onClick={isOwner ? undefined : handleContact}
                    disabled={isOwner}
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {isOwner ? 'This is your listing' : isBuyRequest ? 'Contact Buyer' : 'Contact Seller'}
                  </Button>
                  {isBuyRequest && !isOwner && (
                    <Button 
                      variant="outline"
                      size="lg"
                      className="min-h-[48px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        const event = new CustomEvent('open-engine-buy-request-response', { detail: { request: listing } });
                        window.dispatchEvent(event);
                      }}
                    >
                      <DollarSign className="w-5 h-5 mr-2" />
                      Offer Your Engine
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {renderPhotoGallery()}
            {sellerProfile && (
              <SellerSection profile={sellerProfile} className="mb-4" onNavigate={() => onOpenChange(false)} />
            )}
            {renderTabs()}
          </div>
        </ScrollArea>

        {conversationId && (
          <DocumentRequestModal 
            open={showDocRequestModal} 
            onOpenChange={setShowDocRequestModal} 
            listingId={listing.id} 
            conversationId={conversationId}
            onRequestSent={async (documentIds, message) => { 
              await createRequest(listing.id, documentIds, message); 
              toast({ title: "Request Sent", description: "The seller will be notified." }); 
            }} 
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EngineListingDetailModal;
